/**
 * @file: The drag and the resize of a bar in the grid.
 *
 * A gesture changes the times of an interval. During the gesture, FullCalendar
 * calls eventOverlap to test if two bars can overlap. This class refuses an
 * overlap of two intervals with the same priority. At the drop, the class
 * writes the new times. When the write fails, the class undoes the change.
 *
 * FullCalendar copies the events when the operator presses on a bar. A fetch
 * after the press makes the undo of FullCalendar invalid. Each event has the
 * generation of its fetch, so the drop can tell if a fetch occurred.
 *
 * CalendarManager owns one instance. The options of the grid connect the
 * FullCalendar hooks to it.
 */

import moment from 'moment'
import { describeSaveError } from '../save-error'
import { calendarEventId } from 'common/calendar-event-id'
import { sameIntervalId } from '../calendar-intervals'
import { isSchedulable } from '../../../../shared/on-screen-segments'

/**
 * The words for one gesture that changes an interval's times.
 *
 * A drag and a resize do the same work: they read the event's new times and
 * write them. The two handlers were the same 87 lines apart from these four
 * strings, so the strings are the parameter and the work is written once.
 */
const TIME_GESTURES = Object.freeze({
  drop: Object.freeze({
    noData: 'No interval data for dropped event',
    applied: 'Event dropped:',
    failed: 'Failed to update interval after drop:',
    refused: 'Could not move the interval'
  }),
  resize: Object.freeze({
    noData: 'No interval data for resized event',
    applied: 'Event resized:',
    failed: 'Failed to update interval after resize:',
    refused: 'Could not resize the interval'
  })
})

export class TimeGestures {
  constructor (calendarManager, eventFeed, SceneCollectionService, touchTravel) {
    this.calendarManager = calendarManager
    this.controller = calendarManager.controller
    this.eventFeed = eventFeed
    this.SceneCollectionService = SceneCollectionService
    this.touchTravel = touchTravel
    // The bar that refused the last position of the gesture, or null. A
    // position that passes the rule clears it. Each new gesture also clears it.
    this.refusedAgainst = null
  }

  /**
   * Tell if these two intervals can overlap.
   *
   * Two intervals with the same priority have no defined winner. addInterval
   * refuses that state. It is better to refuse the gesture than to let the
   * operator find the problem in a failed save.
   *
   * FullCalendar calls this method for each overlapping pair, and only when the
   * two ranges intersect. The test is strict, so two intervals end to end do not
   * come here.
   */
  mayOverlap (stillEvent, movingEvent) {
    const still = stillEvent && stillEvent.extendedProps
    const moving = movingEvent && movingEvent.extendedProps

    if (!still || !moving) return true

    // A fetch during the gesture puts a new copy of the dragged interval in the
    // store. FullCalendar excludes only the events of the press, so the bar
    // comes here with its own copy. An interval cannot clash with itself.
    if (sameIntervalId(still.intervalId, moving.intervalId)) return true

    // An interval with no priority never reaches the screen, so two of them
    // share no stretch to be undefined about. The server agrees:
    // findSamePriorityOverlaps returns nothing for a candidate without one. This
    // rule refused such a gesture, because Number(null) is 0 on both sides, and
    // it allowed the same gesture for two undefined values, because NaN is not
    // equal to NaN.
    if (!isSchedulable(still) || !isSchedulable(moving)) return true
    if (Number(still.priority) !== Number(moving.priority)) return true

    // The stop hooks do not tell why a gesture was refused, so the obstacle is
    // recorded here. FullCalendar calls this method on every pointer move.
    // allowPosition clears the obstacle when a later position passes the rule.
    this.refusedAgainst = stillEvent.title
    return false
  }

  /**
   * Forget the obstacle of an earlier position or an earlier gesture.
   *
   * A message about an old obstacle names a playlist that is not in the way.
   */
  forgetRefusedGesture () {
    this.refusedAgainst = null
  }

  /**
   * Accept a position that passed the overlap rule. Refuse every position of
   * the interval that the panel saves.
   *
   * FullCalendar calls eventAllow only after eventOverlap accepted each bar at
   * the position. This includes the start position. Thus a drag that crosses an
   * equal-priority bar and then comes back has no obstacle at its end.
   *
   * A gesture save sends the playlist of the cached event and no updatedAt.
   * If it lands after the save of the panel, it writes back the old playlist.
   * At a refused position, FullCalendar 6.1.21 shows the not-allowed cursor.
   * At the drop it moves the bar back and calls no eventDrop.
   *
   * @param {Object} draggedEvent - The event of the gesture, from the grid
   * @returns {boolean} False while the panel saves this interval
   */
  allowPosition (draggedEvent) {
    this.forgetRefusedGesture()

    const extendedProps = draggedEvent && draggedEvent.extendedProps

    return !extendedProps || !this.controller.isSavingInterval(extendedProps.intervalId)
  }

  /**
   * Start a drag or a resize with no obstacle from an earlier gesture.
   *
   * The fetch generation does not come from this hook. FullCalendar copies the
   * events at the press, and it calls this hook only after the pointer moves.
   */
  startGesture () {
    this.forgetRefusedGesture()
  }

  /**
   * Tell the operator why a drag or a resize was refused.
   *
   * FullCalendar calls the stop hooks at the end of each gesture. An obstacle
   * remains only when the last position was refused. In that case FullCalendar
   * moves the bar back and saves nothing.
   */
  explainRefusedGesture (verb) {
    const against = this.refusedAgainst
    this.refusedAgainst = null

    if (!against) return

    const message = `This interval cannot be ${verb} over ${against}. ${against} has the same role ` +
      'at this time. To take the shared stretch, open the interval, type the new times, click Put on screen and save.'

    this.controller._$log.info('[ScheduleCalendar] Gesture refused against:', against)

    this.controller._toaster.warning(message, 'Cannot overlap')

    this.controller._$scope.$applyAsync()
  }

  /**
   * Tell the operator why the server refused a drag or a resize.
   *
   * The wording is the same as in the panel, because the refusal is the same.
   * The message gives the times of the intervals that clash.
   *
   * A toaster is used instead of an alert. The grid stays readable behind a
   * toaster, and the operator can see the bar move back.
   *
   * @param {Object} error - The rejection, with the conflicts
   * @param {string} title - The name of the gesture
   */
  reportGestureFailure (error, title) {
    this.controller._toaster.error(describeSaveError(error), title)
  }

  /**
   * Undo a drag or a resize after the save failed.
   *
   * The revert() of FullCalendar sends MERGE_EVENTS with the events from the
   * press. The merge uses the defId as the key. A fetch gives each event a new
   * defId, so a revert after a fetch adds the old event as a second bar. In
   * that case, read the week from the server.
   *
   * @param {Object} info - The hook argument of FullCalendar
   */
  undoGesture (info) {
    // The code below sends actions to the event store.
    if (!this.calendarManager.calendar) {
      this.controller._$log.info('[ScheduleCalendar] No calendar to revert into - ignoring')
      return
    }

    if (this.eventFeed.isFromLatestFetch(info.event)) {
      this.controller._$log.info('[ScheduleCalendar] Reverting change via FullCalendar')
      info.revert()
      return
    }

    this.controller._$log.info('[ScheduleCalendar] Events were refetched since the press - resyncing instead of reverting')
    this.calendarManager.scheduleRefetch(0)
  }

  /**
   * Save the new times after a drag or a resize.
   *
   * eventOverlap already refused a gesture that would put two intervals with
   * the same priority in overlap, so this makes no check of its own. The server
   * applies the rule again, and it also applies it to the scenes the client did
   * not fetch.
   *
   * @param {Object} info - The hook argument of FullCalendar
   * @param {Object} words - One entry of TIME_GESTURES
   */
  applyTimeGesture (info, words) {
    const event = info.event
    const extendedProps = event.extendedProps

    if (!extendedProps || !extendedProps.intervalId) {
      this.controller._$log.warn(`[ScheduleCalendar] ${words.noData}`)
      info.revert()
      return
    }

    const newStart = moment(event.start).format() // ISO 8601 with timezone
    const newEnd = moment(event.end).format() // ISO 8601 with timezone

    this.controller._$log.info(`[ScheduleCalendar] ${words.applied}`, {
      intervalId: extendedProps.intervalId,
      oldStart: info.oldEvent.start,
      newStart: event.start,
      oldEnd: info.oldEvent.end,
      newEnd: event.end
    })

    // FullCalendar puts the events from the press back at the drop. After a
    // fetch during the gesture, the store holds a second copy of the bar. The
    // event has the generation of the fetch that the copies come from.
    const fetchedDuringGesture = !this.eventFeed.isFromLatestFetch(event)

    // Two handlers, so an error after a good save cannot undo the drag.
    this.updateIntervalTime(extendedProps.intervalId, newStart, newEnd)
      .then(saved => {
        const updatedAt = saved && saved.updatedAt

        // A fetch after the save reads the new row and removes the second copy.
        if (fetchedDuringGesture) {
          this.calendarManager.scheduleRefetch(0)
        } else if (updatedAt && this.eventFeed.isFromLatestFetch(event)) {
          // A later click on the bar reads this value. After a fetch, the bar
          // is a new event that has the value already.
          event.setExtendedProp('updatedAt', updatedAt)
        }

        this.controller.followSavedTimes(event, updatedAt)
      }, error => {
        this.controller._$log.error(`[ScheduleCalendar] ${words.failed}`, error)
        this.undoGesture(info)

        if (this.calendarManager.calendar) {
          this.reportGestureFailure(error, words.refused)
        }
      })
      .catch(error => {
        this.controller._$log.error('[ScheduleCalendar] The panel did not take the saved times:', error)
      })
  }

  /**
   * Save a drag, or undo a drag from a finger that did not move.
   *
   * On touch, FullCalendar starts the drag after the long press with no move.
   * It drops the bar at the snap of the centre of the bar, not of the finger.
   * Thus a long press can move the bar. The operator did not move the bar, so
   * the drop is undone with no message.
   *
   * A resize is not checked here. A resize starts only after a move of 5 px,
   * and FullCalendar uses the point of the finger for it.
   */
  handleEventDrop (info) {
    if (this.touchTravel.isStillTouch(info.jsEvent)) {
      this.controller._$log.info('[ScheduleCalendar] Drop from a finger that did not move - not saved')
      this.undoGesture(info)
      return
    }

    this.applyTimeGesture(info, TIME_GESTURES.drop)
  }

  handleEventResize (info) {
    this.applyTimeGesture(info, TIME_GESTURES.resize)
  }

  /**
   * Write the new times of an interval to the API.
   *
   * The caller undoes the change if the write fails.
   *
   * @param {number} intervalId - The id of the interval to update
   * @param {string} newStart - The new start time, ISO 8601 with the offset
   * @param {string} newEnd - The new end time, ISO 8601 with the offset
   * @returns {Promise} The promise of the API call
   */
  updateIntervalTime (intervalId, newStart, newEnd) {
    // The collection comes from the cached event. The priority does not: a move
    // changes the times only, and addInterval keeps the stored priority when the
    // payload has none. A priority read from the cache was an invented number
    // for a row that had none.
    const calendarEvent = this.calendarManager.calendar
      ? this.calendarManager.calendar.getEventById(calendarEventId(intervalId))
      : null
    let finalSceneCollectionId = null

    if (calendarEvent && calendarEvent.extendedProps) {
      finalSceneCollectionId = calendarEvent.extendedProps.sceneCollectionId
    } else {
      this.controller._$log.warn(
        '[ScheduleCalendar] No calendar event for intervalId, cannot resolve its collection:',
        intervalId)
    }

    const intervalData = {
      id: intervalId,
      sceneCollectionId: finalSceneCollectionId,
      interval_start: newStart,
      interval_end: newEnd
    }

    // Reject, do not throw. Callers add .catch() to the returned promise. A
    // synchronous throw happens before the caller adds that handler.
    if (!intervalData.sceneCollectionId) {
      return Promise.reject(new Error(`Missing sceneCollectionId for interval ${intervalId}`))
    }
    this.controller._$log.info('[ScheduleCalendar] Updating interval time:', {
      intervalId,
      intervalData,
      // A conflict resolution can run at the same time as the teardown that
      // sets this.calendarManager.calendar to null.
      calendarEventExists: this.calendarManager.calendar ? !!this.calendarManager.calendar.getEventById(calendarEventId(intervalId)) : false,
      resolvedCollection: finalSceneCollectionId
    })

    return this.SceneCollectionService.editInterval(intervalData)
      .then(updatedInterval => {
        this.controller._$log.info('[ScheduleCalendar] Interval time updated successfully:', updatedInterval)

        // The WebSocket update starts the refetch, so no refetch is necessary
        // here.
        return updatedInterval
      })
      .catch(error => {
        this.controller._$log.error('[ScheduleCalendar] Failed to update interval time:', error)
        throw error // The caller does the undo.
      })
  }
}
